import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Panel } from '../../store';
import type { UiV2State } from '../../lib/api';

export type PanelFrameMode = 'side' | 'sideWide' | 'sideXWide' | 'sheet' | 'full';

export type UiVersionContextValue = {
  version: 'v1' | 'v2';
  ui: UiV2State | null;
  /** Force V1 via ?ui=v1 (admin only). */
  forceV1: boolean;
  panelFrameMode: (panel: Panel) => PanelFrameMode;
};

const UiVersionContext = createContext<UiVersionContextValue>({
  version: 'v1',
  ui: null,
  forceV1: false,
  panelFrameMode: () => 'side',
});

const SIDE: Panel[] = [
  'territory',
  'resources',
  'buildings',
  'empire',
  'alliances',
  'profile',
  'poi',
  'suggestions',
  'shortcuts',
  'auth',
];
const SIDE_WIDE: Panel[] = ['onu', 'market', 'leaderboard', 'chat', 'admin'];
/** Arbres techno : besoin de quasi toute la largeur desktop. */
const SIDE_XWIDE: Panel[] = ['technologies'];
const SHEET: Panel[] = ['territory', 'poi', 'auth'];

export function panelFrameModeFor(panel: Panel, isMobile: boolean, isNarrowTablet: boolean): PanelFrameMode {
  if (panel === 'none') return 'side';
  if (isMobile) {
    if (SHEET.includes(panel)) return 'sheet';
    return 'full';
  }
  if (isNarrowTablet) {
    if (SIDE_XWIDE.includes(panel) || SIDE_WIDE.includes(panel) || panel === 'buildings' || panel === 'empire') {
      return 'full';
    }
    if (SHEET.includes(panel)) return 'sheet';
    return 'side';
  }
  if (SIDE_XWIDE.includes(panel)) return 'sideXWide';
  if (SIDE_WIDE.includes(panel)) return 'sideWide';
  if (SIDE.includes(panel)) return 'side';
  return 'side';
}

export function UiVersionProvider({
  version,
  ui,
  forceV1,
  isMobile,
  isNarrowTablet,
  children,
}: {
  version: 'v1' | 'v2';
  ui: UiV2State | null;
  forceV1: boolean;
  isMobile: boolean;
  isNarrowTablet: boolean;
  children: ReactNode;
}) {
  const value = useMemo<UiVersionContextValue>(
    () => ({
      version,
      ui,
      forceV1,
      panelFrameMode: (panel) => panelFrameModeFor(panel, isMobile, isNarrowTablet),
    }),
    [version, ui, forceV1, isMobile, isNarrowTablet],
  );
  return <UiVersionContext.Provider value={value}>{children}</UiVersionContext.Provider>;
}

export function useUiVersion(): UiVersionContextValue {
  return useContext(UiVersionContext);
}
